/**
 * ConversationList.jsx
 * The middle column: everything waiting, comments and messages together.
 *
 * A row shows only what changes a decision — who, what they said, how long ago,
 * and any judgement that has been made about it. Anything the model wrote
 * carries a sparkle, so a reader can always tell a machine's opinion from a
 * colleague's.
 *
 * Dependencies: shared.js, components/Icons.jsx
 */

import { Chat, Sparkle, Check, Warning } from '../Icons.jsx';
import { PLATFORMS } from '../dashboard/helpers.js';
import { INTENT, KINDS, PRIORITY, SENTIMENT, initials, snip, when } from './shared.js';

/** The person's avatar. Meta gives no picture with a comment, so initials it is. */
function Avatar({ name, platform }) {
  const meta = PLATFORMS[platform];
  const Mark = meta?.Icon;

  return (
    <span className="conv__avatar">
      <span className="conv__initials">{initials(name)}</span>
      {Mark && (
        <span className={`conv__platform ${meta.className}`} title={meta.label}>
          <Mark size={11} />
          <span className="sr-only">{meta.label}</span>
        </span>
      )}
    </span>
  );
}

export default function ConversationList({
  items = [],
  selected,
  onSelect,
  loading,
  unavailable,
  elsewhere = 0,
  onShowAll,
  sort,
  onSort,
}) {
  if (unavailable) {
    return (
      <section className="card conv-list">
        <div className="empty empty--inline">
          <div className="empty__art empty__art--warn">
            <Warning size={26} />
          </div>
          <h3>{unavailable.title}</h3>
          <p>{unavailable.body}</p>
        </div>
      </section>
    );
  }

  return (
    <section className={`card conv-list${loading ? ' is-loading' : ''}`}>
      <div className="conv-list__head">
        <h2>Conversations</h2>
        <select
          className="select select--sm"
          value={sort}
          onChange={(e) => onSort(e.target.value)}
          aria-label="Sort"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="priority">Priority</option>
        </select>
      </div>

      {items.length === 0 ? (
        <div className="empty empty--inline">
          <div className="empty__art">
            <Chat size={26} />
          </div>
          <h3>Nothing here</h3>
          <p>
            Nothing matches this filter for the brand you are looking at. Comments and messages
            arrive on the minute sync, or when you press Refresh.
          </p>

          {/* An empty brand and an empty account look identical, and only one
              of them is a reason to stop looking. */}
          {elsewhere > 0 && (
            <button className="btn btn--primary" onClick={onShowAll}>
              Show all brands ({elsewhere} more)
            </button>
          )}
        </div>
      ) : (
        <ul className="conv-items">
          {items.map((item) => {
            const key = `${item.kind}:${item.ref_id}`;
            const kind = KINDS[item.kind];
            const sentiment = item.sentiment ? SENTIMENT[item.sentiment] : null;
            const intent = item.intent ? INTENT[item.intent] : null;
            const priority = PRIORITY[item.priority];

            return (
              <li key={key}>
                <button
                  type="button"
                  className={`conv${selected === key ? ' is-selected' : ''}${
                    item.status === 'resolved' ? ' is-resolved' : ''
                  }`}
                  onClick={() => onSelect(item)}
                >
                  <Avatar name={item.who} platform={item.platform} />

                  <span className="conv__body">
                    <span className="conv__top">
                      <span className="conv__who">{item.who || 'Someone'}</span>
                      <span className="conv__when">{when(item.at)}</span>
                    </span>

                    <span className="conv__text">{snip(item.body) || <em>No text</em>}</span>

                    <span className="conv__chips">
                      {/* Judged by a model: marked, always. */}
                      {intent && (
                        <span className={`tag tag--${intent.tone}`} title="Judged by AI">
                          <Sparkle size={10} /> {intent.label}
                        </span>
                      )}
                      {sentiment && sentiment.tone !== 'muted' && (
                        <span className={`tag tag--${sentiment.tone}`} title="Judged by AI">
                          <Sparkle size={10} /> {sentiment.label}
                        </span>
                      )}
                      {/* Chosen by a person: no sparkle. */}
                      {item.priority !== 'normal' && (
                        <span className={`tag tag--${priority.tone}`}>{priority.label}</span>
                      )}
                      {item.tags.map((t) => (
                        <span className="tag" key={t}>
                          {t}
                        </span>
                      ))}
                      {item.assignee && (
                        <span className="tag" title={`Assigned to ${item.assignee}`}>
                          {item.assignee.split('@')[0]}
                        </span>
                      )}
                    </span>
                  </span>

                  <span className="conv__end">
                    {item.status === 'resolved' ? (
                      <span className="conv__dot conv__dot--done" title="Resolved">
                        <Check size={12} />
                      </span>
                    ) : (
                      !item.answered && <span className="conv__dot" title="No reply yet" />
                    )}
                    {item.note_count > 0 && (
                      <span className="conv__notes" title={`${item.note_count} internal notes`}>
                        {item.note_count}
                      </span>
                    )}
                    <span className="conv__kind" title={`${kind.label} — ${kind.sub}`}>
                      <kind.Icon size={12} />
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
